

import type { ActiveVote, PendingActionData, VoteResolvedData } from "./EventTypes";
import { attemptRefresh } from "../auth/authFetch";

export interface EventStreamCallbacks {
  onNewVote: (vote: ActiveVote) => void;
  onVoteResolved: (data: VoteResolvedData) => void;
  onPendingAction: (action: PendingActionData) => void;
  onConnected?: () => void;
  onError?: (error: Event) => void;
  onAuthExpired?: () => void;
}

export interface EventStreamOptions {
  authCheckUrl?: string;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
}

export class EventStream {
  private source: EventSource | null = null;
  private url: string;
  private reconnectTimer: number | null = null;
  private currentBackoff: number;
  private readonly initialBackoff: number;
  private readonly maxBackoff: number;
  private stopped = false;

  constructor(
    apiBaseUrl: string,
    private callbacks: EventStreamCallbacks,
    private options: EventStreamOptions = {},
  ) {
    this.url = `${apiBaseUrl}/stream`;
    this.initialBackoff = options.initialBackoffMs ?? 1000;
    this.maxBackoff = options.maxBackoffMs ?? 30000;
    this.currentBackoff = this.initialBackoff;
  }

  connect(): void {
    if (this.source) return;
    this.stopped = false;

    this.source = new EventSource(this.url, { withCredentials: true });

    this.source.onopen = () => {
      console.log("[EventStream] connected");
      this.currentBackoff = this.initialBackoff;
      this.callbacks.onConnected?.();
    };

    this.source.onerror = (e) => {
      console.warn("[EventStream] error", e);
      this.callbacks.onError?.(e);
      this.scheduleReconnect();
    };

    this.source.addEventListener("new_vote", (e: MessageEvent) => {
      try {
        const vote: ActiveVote = JSON.parse(e.data);
        this.callbacks.onNewVote(vote);
      } catch (err) {
        console.error("[EventStream] failed to parse new_vote:", err);
      }
    });

    this.source.addEventListener("vote_resolved", (e: MessageEvent) => {
      try {
        const data: VoteResolvedData = JSON.parse(e.data);
        this.callbacks.onVoteResolved(data);
      } catch (err) {
        console.error("[EventStream] failed to parse vote_resolved:", err);
      }
    });

    this.source.addEventListener("pending_action", (e: MessageEvent) => {
      try {
        const action: PendingActionData = JSON.parse(e.data);
        this.callbacks.onPendingAction(action);
      } catch (err) {
        console.error("[EventStream] failed to parse pending_action:", err);
      }
    });
  }

  disconnect(): void {
    this.stopped = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.source) {
      this.source.close();
      this.source = null;
      console.log("[EventStream] disconnected");
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    if (this.reconnectTimer !== null) return;
    if (this.source) {
      this.source.close();
      this.source = null;
    }
    const delay = this.currentBackoff;
    this.currentBackoff = Math.min(this.currentBackoff * 2, this.maxBackoff);
    console.log(`[EventStream] reconnect in ${delay}ms (next backoff ${this.currentBackoff}ms)`);
    this.reconnectTimer = window.setTimeout(async () => {
      this.reconnectTimer = null;
      if (this.stopped) return;
      const authOk = await this.checkAuth();
      if (!authOk) {
        console.warn("[EventStream] auth expired, giving up reconnect");
        this.stopped = true;
        this.callbacks.onAuthExpired?.();
        return;
      }
      this.connect();
    }, delay);
  }

  private async checkAuth(): Promise<boolean> {
    if (!this.options.authCheckUrl) return true;
    try {
      const r = await fetch(this.options.authCheckUrl, { credentials: "include" });
      if (r.ok) return true;
      return await attemptRefresh();
    } catch {
      return true;
    }
  }

  get connected(): boolean {
    return this.source?.readyState === EventSource.OPEN;
  }
}
